import { isPastEvent } from './eventMeta'
import type { EventRecord } from '../api/types'

/**
 * What the booking button says and whether it does anything.
 *
 * Ported from wechat/src/utils/booking.js unchanged. Both clients import THIS,
 * so the mini program and the web page can never disagree about whether a seat
 * is bookable — which is the kind of difference nobody notices until a member
 * books a dinner that already happened.
 */

/** Match statuses that mean the user actually holds a seat. */
export const HOLDS_SEAT = ['accepted', 'booked', 'attended'] as const

export type BookingMode = 'free' | 'pay_later' | 'prepaid'

/** Legacy rows predate booking_mode, so fall back to the price. */
export function bookingModeOf(event?: Partial<EventRecord> | null): BookingMode {
  if (!event) return 'free'
  return (event.booking_mode as BookingMode) || (event.price ? 'prepaid' : 'free')
}

/**
 * Can the user undo this themselves?
 * free + pay_later: yes, nothing has been charged.
 * prepaid: no — money changed hands, so it goes through us for the refund.
 */
export function canSelfCancel(mode: BookingMode): boolean {
  return mode === 'free' || mode === 'pay_later'
}

/**
 * Is the booking button live at all? A finished dinner can be neither joined
 * nor cancelled — there is no seat left to release, and the server returns 410
 * for both.
 */
export function canBook(event?: Partial<EventRecord> | null): boolean {
  return !isPastEvent(event)
}

export interface SeatMatch { event_id: string; status: string }

/** Picks out the user's active match for an event. */
export function findSeat<T extends SeatMatch>(matches: T[] | null | undefined, eventId: string): T | null {
  return (matches || []).find(
    (m) => m.event_id === eventId && (HOLDS_SEAT as readonly string[]).includes(m.status)
  ) || null
}
